/**
 * E59 — basemap tile client, DOM-free by design so the fallback state machine
 * is unit-testable from the API suite (the registry's canvas rule 4 demands a
 * simulated-WMS-failure test proving the Esri fallback).
 *
 * The SOURCE decision is the server's (`GET …/basemap` — bundeslandResolver +
 * registry + Decision-Graph record). This module only turns that decision into
 * tile URLs, enforces the per-service request cap (rule 5), and degrades to
 * Esri on failure without ever leaving the canvas blank.
 */

export type ClientBasemap =
  | {
      kind: "wms";
      providerId: string;
      endpoint: string;
      layer: string | null;
      format: string;
      maxRequestPx: number;
      attribution: string;
      vintageNote?: string;
      /** E59 v2.2.1 — params pinned into EVERY GetMap request. HH: TIME=2022
       *  (the service default is the closed-licence 2023 Maxar vintage —
       *  omitting TIME is a licence breach, not a rendering bug). There is
       *  exactly one URL builder and it always appends these. */
      fixedParams?: Record<string, string>;
      /** Above this OGC scale denominator the layer returns nothing (HH:
       *  5000) → those zoom levels render Esri instead of a blank canvas. */
      maxScaleDenominator?: number | null;
      /** E62 — the data's ground resolution (m/pixel): 0.2 for DOP20 states,
       *  0.1 for NRW. Drives the native zoom cap and the on-canvas indicator. */
      groundResolutionM?: number;
    }
  | { kind: "esri"; providerId: string; attribution: string };

export const ESRI_CLIENT_BASEMAP: ClientBasemap = {
  kind: "esri",
  providerId: "esri_world_imagery",
  attribution: "Esri World Imagery",
};

export interface BasemapState {
  source: ClientBasemap;
  /** Set once a WMS tile fails to load — from then on every tile is Esri.
   *  Rule 5: never a blank canvas. */
  failed: boolean;
}

export function initialBasemapState(source: ClientBasemap | null): BasemapState {
  return { source: source ?? ESRI_CLIENT_BASEMAP, failed: false };
}

/** WMS failure observed (tile error) → permanent Esri fallback this session. */
export function markBasemapFailed(s: BasemapState): BasemapState {
  return { ...s, failed: true };
}

const RE = 6378137;
const WEB_MERCATOR_EXTENT = Math.PI * RE;
/** OGC standardized rendering pixel: 0.28 mm. */
const OGC_PIXEL_M = 0.00028;

/** OGC scale denominator of one XYZ tile at zoom z rendered at tilePx. */
export function scaleDenominatorFor(z: number, tilePx = 512): number {
  const metresPerPixel = (2 * WEB_MERCATOR_EXTENT) / Math.pow(2, z) / tilePx;
  return metresPerPixel / OGC_PIXEL_M;
}

/** The source whose pixels will actually render at zoom z (drives attribution
 *  too). E59 v2.2.1: a WMS with a MaxScaleDenominator (HH 1:5000) is empty
 *  when zoomed out — those levels render Esri, never a blank canvas. */
export function activeSource(s: BasemapState, z?: number): ClientBasemap {
  if (s.source.kind !== "wms" || s.failed || s.source.layer === null) return ESRI_CLIENT_BASEMAP;
  if (
    z !== undefined &&
    s.source.maxScaleDenominator != null &&
    scaleDenominatorFor(z) > s.source.maxScaleDenominator
  ) {
    return ESRI_CLIENT_BASEMAP;
  }
  return s.source;
}

export function esriTileUrl(z: number, ty: number, tx: number): string {
  return `https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/${z}/${ty}/${tx}`;
}

/**
 * GetMap URL for one XYZ-grid tile in EPSG:3857 (axis order x,y in WMS 1.3.0
 * for projected CRS). `tilePx` must respect the service cap — rule 5.
 */
export function wmsTileUrl(
  src: Extract<ClientBasemap, { kind: "wms" }>,
  z: number,
  ty: number,
  tx: number,
  tilePx = 512,
): string {
  if (src.layer === null) throw new Error(`basemap ${src.providerId}: layer not pinned — source is dormant`);
  if (tilePx > src.maxRequestPx) {
    throw new Error(`basemap ${src.providerId}: ${tilePx}px exceeds the service cap ${src.maxRequestPx}px`);
  }
  const worldM = 2 * WEB_MERCATOR_EXTENT;
  const tileM = worldM / Math.pow(2, z);
  const minx = -WEB_MERCATOR_EXTENT + tx * tileM;
  const maxx = minx + tileM;
  const maxy = WEB_MERCATOR_EXTENT - ty * tileM;
  const miny = maxy - tileM;
  const sep = src.endpoint.includes("?") ? (src.endpoint.endsWith("?") || src.endpoint.endsWith("&") ? "" : "&") : "?";
  const params = new URLSearchParams({
    SERVICE: "WMS",
    VERSION: "1.3.0",
    REQUEST: "GetMap",
    LAYERS: src.layer,
    STYLES: "",
    CRS: "EPSG:3857",
    BBOX: `${minx.toFixed(2)},${miny.toFixed(2)},${maxx.toFixed(2)},${maxy.toFixed(2)}`,
    WIDTH: String(tilePx),
    HEIGHT: String(tilePx),
    FORMAT: src.format,
    TRANSPARENT: "FALSE",
    // E59 v2.2.1 — licence-pinned params (HH: TIME=2022). This spread is the
    // ONLY URL builder path, so a request without them cannot be constructed.
    ...(src.fixedParams ?? {}),
  });
  return `${src.endpoint}${sep}${params.toString()}`;
}

/** One call the canvas uses per tile — always returns a URL (never blank). */
export function tileUrlFor(s: BasemapState, z: number, ty: number, tx: number): string {
  const src = activeSource(s, z);
  return src.kind === "wms" ? wmsTileUrl(src, z, ty, tx) : esriTileUrl(z, ty, tx);
}

/* ---------------- E62 — native resolution: cap + indicator ---------------- */

/** The Esri XYZ pyramid ends at z19 — the only source with a zoom LITERAL;
 *  WMS sources derive their cap from the data's ground resolution. */
export const ESRI_MAX_ZOOM = 19;

/**
 * E62.2 — the deepest zoom at which one 256-based tile pixel still carries
 * real data: smallest z with tileRes(z) ≤ groundResolutionM at this latitude.
 * 20 cm → z19, NRW's 10 cm → z20 (the hardcoded 19 came from Esri's pyramid
 * and discarded half of NRW's detail — WMS renders at any requested z).
 */
export function maxNativeZoom(src: ClientBasemap, latDeg: number): number {
  if (src.kind !== "wms" || !src.groundResolutionM) return ESRI_MAX_ZOOM;
  const cosL = Math.cos((latDeg * Math.PI) / 180);
  // 5 % tolerance: at southern-BY latitudes the z19 tile pixel is 0.2007 m —
  // within the data's own accuracy, not a reason to burn a whole extra zoom
  // level (20 cm → z19, 10 cm → z20 across each state's latitude span).
  return Math.ceil(Math.log2((2 * WEB_MERCATOR_EXTENT * cosL) / (src.groundResolutionM * 1.05 * 256)) - 1e-9);
}

/**
 * E62 — the ONE tile-zoom computation for the canvas (replaces the hardcoded
 * `min(19, …)`): view-driven zoom, clamped to the ACTIVE source's native
 * ceiling. Beyond it the imagery renders at native and only scales — the
 * indicator (imageryAtLimit) tells the planner which limit they've hit.
 */
export function tileZoomFor(s: BasemapState, S: number, latDeg: number): number {
  const cosL = Math.cos((latDeg * Math.PI) / 180);
  const ideal = Math.round(Math.log2((2 * WEB_MERCATOR_EXTENT * cosL * S) / 256));
  // which source actually renders at the ideal zoom decides the ceiling
  // (HH gated to Esri when zoomed out → Esri's ceiling applies there)
  const zMax = maxNativeZoom(activeSource(s, Math.max(14, ideal)), latDeg);
  return Math.max(14, Math.min(zMax, ideal));
}

/**
 * E62.1 — is the view magnified past the imagery's native resolution?
 * True once a screen pixel is finer than a data pixel (1/S < groundRes) —
 * from there on the planner sees interpolation, not data, and must be told
 * (Romina read 7×-magnified 20 cm pixels as "degraded imagery"). Returns the
 * source's resolution for display; null resolution for Esri (no verified
 * figure to claim — the indicator still shows, without a number).
 */
export function imageryAtLimit(
  s: BasemapState,
  S: number,
  latDeg: number,
): { resolutionM: number | null } | null {
  const src = activeSource(s, tileZoomFor(s, S, latDeg));
  const screenResM = 1 / S;
  if (src.kind === "wms" && src.groundResolutionM) {
    return screenResM < src.groundResolutionM / 1.02 ? { resolutionM: src.groundResolutionM } : null;
  }
  // Esri: past the pyramid's end (z19) the same magnification problem exists
  const cosL = Math.cos((latDeg * Math.PI) / 180);
  const esriNativeResM = (2 * WEB_MERCATOR_EXTENT * cosL) / (256 * Math.pow(2, ESRI_MAX_ZOOM));
  return screenResM < esriNativeResM / 1.02 ? { resolutionM: null } : null;
}

/** Rule 2: the exact credit of the pixels ON SCREEN at this zoom (+ vintage
 *  where exposed — for HH the 2022 year display is REQUIRED, not optional). */
export function attributionFor(s: BasemapState, z?: number): string {
  const src = activeSource(s, z);
  if (src.kind === "wms") return src.vintageNote ? `${src.attribution} · ${src.vintageNote}` : src.attribution;
  return src.attribution;
}
